import React, { useRef, useState } from "react";
import axios from "../../axiosConfig"; // baseURL + withCredentials
import * as XLSX from "xlsx";

const env = window.__ENV || {};

const ActionButtons = ({
  data,
  handleUndo,
  undoStack,
  handleRedo,
  redoStack,
  handleAddRow,
  unsavedChanges,
  setUnsavedChanges,
  onAfterSaveOrUpload,
  accessToken,
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const handleSave = async () => {
    const confirmSave = window.confirm(
      "Once saved, the old data can't be recovered! Are you sure you want to save?"
    );
    if (!confirmSave) return;

    try {
      // Convert JSON to Excel (overwrite the configured REACT_APP_FILE_PATH on server)
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, sheet, "Sheet1");

      const blob = new Blob(
        [XLSX.write(workbook, { type: "array", bookType: "xlsx" })],
        { type: "application/octet-stream" }
      );

      const formData = new FormData();
      formData.append("file", blob, env.REACT_APP_FILE_PATH);

      const { data: message } = await axios.post("/save-file", formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
          }
        },
      });

      alert(message);
      setUnsavedChanges(false);

      // Auto-refresh from server after save
      if (onAfterSaveOrUpload) {
        await onAfterSaveOrUpload();
      }
    } catch (err) {
      console.error("Error saving file:", err);
      alert("Failed to save file.");
    }
    finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(null), 400);
    }
  };

  const handleDownload = () => {
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, sheet, "Sheet1");

    const blob = new Blob(
      [XLSX.write(workbook, { type: "array", bookType: "xlsx" })],
      { type: "application/octet-stream" }
    );

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "updated_data.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ---- new: upload a fresh Excel and let backend overwrite the configured file ----
  const triggerFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);

    try {
      const formData = new FormData();
      // The backend ignores the incoming filename and always writes to REACT_APP_FILE_PATH,
      // but it’s fine to pass the original name here.
      formData.append("file", file, file.name);

      const { data: message } = await axios.post("/save-file", formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        // Optional: show progress for large uploads (2 GB max on server)
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
            // You can wire this up to a progress bar if desired
          }
        },
      });

      alert(message || "File uploaded successfully.");
      setUnsavedChanges(false);
      // Auto-reload after upload
      if (onAfterSaveOrUpload) {
        await onAfterSaveOrUpload();
      }
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Failed to upload new Excel.");
    } finally {
      setUploading(false);
      // reset the input so picking the same file again still triggers onChange
      setTimeout(() => setUploadProgress(null), 400);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2 pt-3">
      {uploadProgress !== null && (
        <div className="w-full">
          <div className="w-full bg-gray-200 rounded h-3 overflow-hidden">
            <div
              className="h-3 bg-blue-500 transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="text-xs text-right text-gray-600 mt-1">
            Uploading... {uploadProgress}%
          </div>
        </div>
      )}
      <button
        disabled={undoStack.length === 0 || uploading}
        onClick={handleUndo}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-blue-500 hover:bg-blue-700 border border-blue-700  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
      >
        Undo
      </button>

      <button
        disabled={redoStack.length === 0 || uploading}
        onClick={handleRedo}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-blue-500 hover:bg-blue-700 border border-blue-700  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
      >
        Redo
      </button>

      <button
        disabled={!unsavedChanges || uploading}
        onClick={handleSave}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-[#1da1f2] hover:bg-[#1da1f2]/90 focus:ring-4 focus:outline-none focus:ring-[#1da1f2]/50 dark:focus:ring-[#1da1f2]/55  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1da1f2]"
      >
        Save Changes
      </button>

      <button
        onClick={handleDownload}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-[#20aaa3] hover:bg-[#20aaa3]/90 focus:ring-4 focus:outline-none focus:ring-[#20aaa3]/50 dark:focus:ring-[#20aaa3]/55"
      >
        Download File
      </button>

      <button
        onClick={handleAddRow}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-[#ffbd2d] hover:bg-[#ffbd2d]/80 focus:ring-2 focus:outline-none focus:ring-[#ffbd2d]/50 dark:focus:ring-[#ffbd2d]/55"
      >
        Add Row
      </button>

      {/* NEW: Upload Excel (overwrites server’s configured file) */}
      <button
        onClick={triggerFilePicker}
        disabled={uploading}
        className="w-full p-1 font-bold rounded-lg shadow-gray-500 shadow-md text-white bg-[#6c5ce7] hover:bg-[#6c5ce7]/90 focus:ring-4 focus:outline-none focus:ring-[#6c5ce7]/50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {uploading ? "Uploading..." : "Upload Excel"}
      </button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xls, application/vnd.ms-excel"
        className="hidden"
        onChange={onFileChosen}
      />
    </div>
  );
};

export default ActionButtons;
